/**
 * InitialLoader.js
 * Talkroute
 *
 * Created by Vel Genov on 2/14/19
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";

import {
  fetchVoicemailBoxes,
  fetchVoicemails,
} from "../actions/voicemailActions";
import { fetchPhoneNumbers } from "../actions/preferencesActions";
import { fetchCallHistoryUpdates } from "../actions/callHistoryActions";
import { fetchBlockedNumbers } from "../actions/callActions";
import { fetchAllConversations } from "../actions/conversationActions";
import { fetchConnectionData } from "../actions/webRTCConnectionActions";
import {
  getUserPermissions,
  setPermittedFeatures,
  setUserRole,
  getUserPreferences,
} from "../actions/userActions";
import UserService from "@services/User.service";
import PlanService from "@services/Plan.service";
import { RootState } from "@helpers/store/store";

declare global {
  interface Window {
    stonlyTrack: (
      identify: string,
      userId: string,
      args: {
        "talkroute-login-type": string | undefined;
        "talkroute-plan": string | undefined;
      }
    ) => void;
  }
}

interface IInitialLoaderProps {
  userTokenReady: boolean;
  userId: number | null;
  impersonationUserId: number | null;
  accountId: number | null;
  fetchVoicemailBoxes: () => any;
  fetchPhoneNumbers: () => any;
  fetchVoicemails: (background?: boolean) => any;
  fetchCallHistoryUpdates: (background?: boolean) => any;
  fetchAllConversations: () => any;
  fetchConnectionData: () => any;
  fetchBlockedNumbers: () => any;
  getUserPreferences: () => any;
  getUserPermissions: () => any;
  setUserRole: (role: string) => any;
  setPermittedFeatures: typeof setPermittedFeatures;
}

/**
 * A React component responsible for the initial data load
 *
 * In the future, this component can be updated and serve as a gate to the rest of the app
 */
class InitialLoader extends React.Component<
  IInitialLoaderProps,
  { userRole?: string; planName?: string, callHistoryLastVisited?: number }
> {
  constructor(props) {
    super(props);

    this.state = {
      userRole: undefined,
      planName: undefined,
      callHistoryLastVisited: undefined,
    };
  }
  /**
   * Request a fetch of the voicemail boxes
   */
  loadVoicemailBoxes = () => {
    const { fetchVoicemailBoxes: fetchVoicemailBoxesLocal } = this.props;
    fetchVoicemailBoxesLocal();
  };

  /**
   * Request a fetch of the user phone numbers
   */
  loadUserPhoneNumbers = () => {
    const { fetchPhoneNumbers: fetchPhoneNumbersLocal } = this.props;
    fetchPhoneNumbersLocal();
  };

  /**
   * Request a fetch of the voicemails
   */
  loadVoicemails = () => {
    const { fetchVoicemails: fetchVoicemailsLocal } = this.props;
    fetchVoicemailsLocal();
  };

  /**
   * Request a fetch of call history
   */
  loadCallHistory = () => {
    const { fetchCallHistoryUpdates: fetchCallHistoryLocal } = this.props;
    fetchCallHistoryLocal();
  };

  /**
   * Request a fetch of the conversations
   */
  loadConversations = () => {
    const { fetchAllConversations: fetchAllConversationsLocal } = this.props;
    fetchAllConversationsLocal();
  };

  loadWebRTCConnectionData = () => {
    const { fetchConnectionData: fetchConnectionDataLocal } = this.props;
    fetchConnectionDataLocal();
  };

  /**
   * Request a fetch of blocked phone numbers
   */
  loadBlockedNumbers = () => {
    const { fetchBlockedNumbers: fetchBlockedNumbersLocal } = this.props;
    fetchBlockedNumbersLocal();
  };

  /**
   * Request a getting user permissions
   */
  loadUserPermissions = () => {
    const { getUserPermissions: getUserPermissionsLocal } = this.props;
    getUserPermissionsLocal && getUserPermissionsLocal();
  };

  getUserRole = async () => {
    const { GetUserDetails } = UserService();
    const id = this.props.impersonationUserId ? this.props.impersonationUserId : this.props.userId;
    try {
      const data = await GetUserDetails(String(id));
      if (data) {
        this.props.setUserRole(data.role);
        this.setState((state) => ({ ...state, userRole: data.role }));
      }
    } catch (error) {
      console.warn(error);
    }
  };

  getUserPreferences = () => {
    const { getUserPreferences: getUserPreferencesLocal } = this.props;
    getUserPreferencesLocal();
  };

  getPermittedFeatures = async (accountId: number) => {
    const { GetPermittedFeatures } = UserService();
    try {
      const { data } = await GetPermittedFeatures(String(accountId));
      this.props.setPermittedFeatures(data);
    } catch (error) {
      throw error;
    }
  };

  getAccountDetails = async (accountId: number) => {
    const impersonation = this.props.impersonationUserId ? true : false;
    if (impersonation) return;
    const { GetAccountDetails } = UserService();
    try {
      const data = await GetAccountDetails(String(accountId));
      await this.getPlan(data.planId);
      return data;
    } catch (err) {
      throw err;
    }
  };

  getPlan = async (planId: number) => {
    const { GetPlan } = PlanService();
    try {
      const data = await GetPlan(String(planId));
      this.setState((state) => ({ ...state, planName: data.planName }));
      return data;
    } catch (err) {
      throw err;
    }
  };

  setStonlyTrack = () => {
    window.stonlyTrack("identify", String(this.props.userId), {
      "talkroute-login-type": this.state.userRole ?? "",
      "talkroute-plan": this.state.planName ?? "",
    });
  };

  /**
   * Lifecycle function
   * Called when the react component mounts
   */
  async componentDidMount() {
    if (this.props.userTokenReady) {
      await this.getUserPreferences();
      this.loadCallHistory();
      this.loadVoicemailBoxes();
      this.loadUserPhoneNumbers();
      this.loadVoicemails();
      this.loadConversations();
      this.loadWebRTCConnectionData();
      this.loadBlockedNumbers();
      this.loadUserPermissions();
      this.getUserRole();
      if (this.props.accountId) {
        this.getPermittedFeatures(this.props.accountId);
        this.getAccountDetails(this.props.accountId);
      }
    }
  }

  /**
   * Lifecycle function
   * Called when the react component updates
   */
  async componentDidUpdate(prevProps: IInitialLoaderProps) {
    if (!prevProps.userTokenReady && this.props.userTokenReady) {
      await this.getUserPreferences();
      this.loadVoicemailBoxes();
      this.loadUserPhoneNumbers();
      this.loadVoicemails();
      this.loadConversations();
      this.loadWebRTCConnectionData();
      this.loadBlockedNumbers();
      this.loadUserPermissions();
      this.getUserRole();
    }
    if (this.props.accountId) {
      this.getPermittedFeatures(this.props.accountId);
    }
    if (this.props.userId && this.state.planName && this.state.userRole) {
      this.setStonlyTrack();
    }
  }

  render() {
    return null;
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state: RootState) => ({
  userTokenReady: state.authentication.userTokenReady,
  userId: state.authentication.userId,
  impersonationUserId: state.impersonateUserReducer.impersonateUserId,
  accountId: state.authentication.accountId,
});

export default connect(mapStateToProps, {
  fetchVoicemailBoxes,
  getUserPreferences,
  fetchPhoneNumbers,
  fetchVoicemails,
  fetchCallHistoryUpdates,
  fetchAllConversations,
  fetchConnectionData,
  fetchBlockedNumbers,
  getUserPermissions,
  setUserRole,
  setPermittedFeatures,
})(InitialLoader);
